import axios from "axios";
import apiClient from "./api/client";
import ENDPOINTS from "./api/endpoints";
import camelcaseKeys from "camelcase-keys";

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8081';

const tenantService = {

    createOrganization: async (orgData) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.CREATE_ORG, orgData);
            return response;
        } catch (error) {
            console.error("Failed to create organization:", error);
            throw error;
        }
    },


    getAllOrganizations: async () => {
        try {
            const response = await apiClient.get(ENDPOINTS.TENANT.SETUP.GET_ALL_ORGS);
            return response;
        } catch (error) {
            console.error("Failed to fetch organizations:", error);
            throw error;
        }
    },


    registerDatabase: async (dbConfig) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.REGISTER_DB, dbConfig);
            return response;
        } catch (error) {
            console.error("Failed to register tenant database:", error);
            throw error;
        }
    },

    updateDatabase: async (dbConfigId, dbData) => {
        try {
            const response = await apiClient.put(ENDPOINTS.TENANT.SETUP.UPDATE_DB(dbConfigId), dbData);
            return response;
        } catch (error) {
            console.error("Failed to update database:", error);
            throw error;
        }
    },


    syncOrganization: async (orgId, branchId) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.SYNC_ORG(orgId, branchId));
            return response;
        } catch (error) {
            console.error("Failed to sync organization:", error);
            throw error;
        }
    },


    createBranch: async (branchData) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.CREATE_BRANCH, branchData);
            return response;
        } catch (error) {
            console.error("Failed to create branch:", error);
            throw error;
        }
    },


    syncBranches: async (orgId) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.SYNC_BRANCHES(orgId));
            return response;
        } catch (error) {
            console.error("Failed to sync branches:", error);
            throw error;
        }
    },


    completeSetup: async (setupRequest) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.COMPLETE_SETUP, setupRequest);
            return response;
        } catch (error) {
            console.error("Failed to complete tenant setup:", error);
            throw error;
        }
    },


    createAdminUser: async (orgId, adminRequest) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.CREATE_ADMIN_USER(orgId), adminRequest);
            return response;
        } catch (error) {
            console.error("Failed to create admin user:", error);
            throw error;
        }
    },


    reloadDataSources: async () => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.SETUP.RELOAD_DATASOURCES);
            return response;
        } catch (error) {
            console.error("Failed to reload data sources:", error);
            throw error;
        }
    },


    runAllMigrations: async () => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.MIGRATIONS.RUN_ALL);
            return response;
        } catch (error) {
            console.error("Failed to run all migrations:", error);
            throw error;
        }
    },


    runTenantMigration: async (orgId, branchId) => {
        try {
            const response = await apiClient.post(ENDPOINTS.TENANT.MIGRATIONS.RUN_FOR_ORG(orgId, branchId));
            return response;
        } catch (error) {
            console.error("Failed to run tenant migration:", error);
            throw error;
        }
    },


    getMigrationStatus: async () => {
        try {
            const response = await apiClient.get(ENDPOINTS.TENANT.MIGRATIONS.STATUS);
            return response;
        } catch (error) {
            console.error("Failed to get migration status:", error);
            throw error;
        }
    },

    // ============================================
    // SUPER ADMIN API METHODS (X-Tenant-Setup-Key)
    // ============================================

    validateSuperAdminToken: async () => {
        try {
            // Now we use the current JWT token to validate
            const response = await apiClient.get(ENDPOINTS.TENANT.SETUP.VALIDATE_TOKEN);
            return response.valid === true;
        } catch (error) {
            console.error("Token validation failed:", error);
            return false;
        }
    },

branchStatus: async (branchId, isActive) => {
    try {
        const response = await apiClient.patch(
            ENDPOINTS.TENANT.SETUP.STATUS,
            {
                branchId,
                isActive
            }
        );
        return response;
    } catch (error) {
        console.error("Failed to update branch status:", error);
        throw error;
    }
},


    superAdminCreateOrganization: async (orgData) => {
        return tenantService.createOrganization(orgData);
    },


    superAdminGetAllOrganizations: async () => {
        return tenantService.getAllOrganizations();
    },


    superAdminCreateBranch: async (branchData) => {
        return tenantService.createBranch(branchData);
    },


    superAdminRegisterDatabase: async (dbConfig) => {
        return tenantService.registerDatabase(dbConfig);
    },


    superAdminRunTenantMigration: async (orgId, branchId) => {
        return tenantService.runTenantMigration(orgId, branchId);
    },

    superAdminSyncOrganization: async (orgId, branchId) => {
        return tenantService.syncOrganization(orgId, branchId);
    },


    superAdminCreateAdminUser: async (orgId, adminRequest) => {
        return tenantService.createAdminUser(orgId, adminRequest);
    },


    superAdminReloadDataSources: async () => {
        return tenantService.reloadDataSources();
    },


    superAdminGetUsers: async (page = 0, size = 20, search = '') => {
        try {
            const response = await apiClient.get(ENDPOINTS.TENANT.SUPER_ADMIN.USERS.GET_ALL, {
                params: { page, size, search }
            });
            return response;
        } catch (error) {
            console.error("Failed to fetch tenant users:", error);
            throw error;
        }
    },

    superAdminUpdateUserStatus: async (id, active) => {
        try {
            const response = await apiClient.patch(ENDPOINTS.TENANT.SUPER_ADMIN.USERS.UPDATE_STATUS(id), { active });
            return response;
        } catch (error) {
            console.error("Failed to update user status:", error);
            throw error;
        }
    },

    superAdminGetMigrationStatus: async () => {
        return tenantService.getMigrationStatus();
    },

    // Fetch all tenant details for a branch (org, branch, db config, addresses)
    getTenantDetails: async (branchId) => {
        try {
            const response = await apiClient.get(ENDPOINTS.TENANT.SETUP.GET_TENANT_DETAILS(branchId));
            return response;
        } catch (error) {
            console.error("Failed to fetch tenant details:", error);
            throw error;
        }
    },

    // Update organization in master database
    updateOrganization: async (orgId, orgData) => {
        try {
            const response = await apiClient.put(ENDPOINTS.TENANT.SETUP.UPDATE_ORG(orgId), orgData);
            return response;
        } catch (error) {
            console.error("Failed to update organization:", error);
            throw error;
        }
    },

    // Update branch in master database
    updateBranch: async (branchId, branchData) => {
        try {
            const response = await apiClient.put(ENDPOINTS.TENANT.SETUP.UPDATE_BRANCH(branchId), branchData);
            return response;
        } catch (error) {
            console.error("Failed to update branch:", error);
            throw error;
        }
    }
};

export default tenantService;

